import { Button, Card, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { TicketReconciliationParameters, TicketReconciliationReportFormat } from '@parkchirp/reporting';
import { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { DailyReportDateFilter } from './DailyReportDateFilter';
import { FacilityHierarchyFilter, FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { reportingPanelStyles } from './styles';
import { ReportFormProps } from './types';

const initialDateFilter: DailyReportDateFilter = {
	reportStartDate: null,
	reportEndDate: null,
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

export const TicketReconciliationForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateFilter, setDateFilter] = useState<DailyReportDateFilter>(initialDateFilter);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<TicketReconciliationReportFormat>('csv');

	const canSubmit = useMemo(() => {
		return Boolean(dateFilter.reportStartDate) && Boolean(dateFilter.reportEndDate);
	}, [dateFilter]);

	const reset = () => {
		setDateFilter(initialDateFilter);
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const buildParameters = (): TicketReconciliationParameters => {
		if (!dateFilter.reportStartDate) {
			throw new Error('Select a start date.');
		}

		const parameters: TicketReconciliationParameters = {
			reportStartDate: (dateFilter.reportStartDate as Dayjs).format('YYYY-MM-DD'),
			format,
		};

		if (dateFilter.reportEndDate) {
			parameters.reportEndDate = (dateFilter.reportEndDate as Dayjs).format('YYYY-MM-DD');
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		return parameters;
	};

	const validate = () => {
		if (!dateFilter.reportStartDate) {
			onValidationError('Please select a start date for the report.');
			return false;
		}

		if (!dateFilter.reportEndDate) {
			onValidationError('Please select an end date for the report.');
			return false;
		}

		if ((dateFilter.reportEndDate as Dayjs).isBefore(dateFilter.reportStartDate as Dayjs)) {
			onValidationError('End date cannot be before the start date.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}
		await createReport({
			reportType: 'ticketReconciliation',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<DailyReportDateFilter value={dateFilter} onChange={setDateFilter} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: TicketReconciliationReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
